import { useRef } from 'react';

export interface IEmbeddedPageOptions {
  hideTopNav?: boolean;
  hideLeftMenu?: boolean;
  hidePageHeader?: boolean;
  hidePageTabs?: boolean;
  hideAgent?: boolean;
  showDetailPageHeader?: boolean;
}

export const EMBEDDED_PAGE_QUERY_PARAMS: Record<keyof IEmbeddedPageOptions, string> = {
  hideTopNav: 'embed_hide_top_nav',
  hideLeftMenu: 'embed_hide_left_menu',
  hidePageHeader: 'embed_hide_page_header',
  hidePageTabs: 'embed_hide_page_tabs',
  hideAgent: 'embed_hide_agent',
  showDetailPageHeader: 'embed_show_detail_page_header',
};

const TRUE_VALUES = new Set(['1', 'true']);

export const parseEmbeddedPageOptions = (search = ''): IEmbeddedPageOptions => {
  const searchParams = new URLSearchParams(search);
  const options: IEmbeddedPageOptions = {};
  (Object.keys(EMBEDDED_PAGE_QUERY_PARAMS) as Array<keyof IEmbeddedPageOptions>)
    .forEach((key) => {
      options[key] = TRUE_VALUES.has(
        String(searchParams.get(EMBEDDED_PAGE_QUERY_PARAMS[key]) || '').toLowerCase(),
      );
    });
  return options;
};

const getEmbeddedPageSearch = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const currentSearch = window.location.search;
  const currentSearchParams = new URLSearchParams(currentSearch);
  const hasEmbeddedPageParams = Object.values(EMBEDDED_PAGE_QUERY_PARAMS)
    .some((paramName) => currentSearchParams.has(paramName));
  if (hasEmbeddedPageParams) {
    return currentSearch;
  }

  try {
    const iframeSrc = window.frameElement?.getAttribute('src');
    return iframeSrc
      ? new URL(iframeSrc, window.location.origin).search
      : currentSearch;
  } catch (e) {
    return currentSearch;
  }
};

export const appendEmbeddedPageOptions = (
  url: string,
  options?: IEmbeddedPageOptions,
) => {
  if (!url || !options) {
    return url;
  }

  const parsedUrl = new URL(url, window.location.origin);
  (Object.keys(EMBEDDED_PAGE_QUERY_PARAMS) as Array<keyof IEmbeddedPageOptions>)
    .forEach((key) => {
      if (options[key] !== undefined) {
        parsedUrl.searchParams.set(
          EMBEDDED_PAGE_QUERY_PARAMS[key],
          options[key] ? '1' : '0',
        );
      }
    });
  return parsedUrl.toString();
};

export const useEmbeddedPageOptions = () => {
  const initialOptionsRef = useRef<IEmbeddedPageOptions | null>(null);
  if (initialOptionsRef.current === null) {
    initialOptionsRef.current = parseEmbeddedPageOptions(getEmbeddedPageSearch());
  }
  return initialOptionsRef.current;
};
